import { Field, Form, FormikProvider, useFormik } from "formik"
import { orderTrackingSchema } from "../schema"
import cn from "../../../utils/cn"
import LabeledText from "./components/LabledText"
import HeaderCell from "./components/HeaderCell"
import { useEffect, useState } from "react"
import {
  useOrdersByAccessionNumber,
  toOrder,
  toResult,
} from "../../../hooks/useOrders"
import Loader from "../../../components/Loader"
import DataCell from "./components/DataCell"
import Modal from "../../../components/Modal"
import { isEmpty } from "lodash"
import ReportMessageText from "./components/ReportMessageText"
import OrderMessageText from "./components/OrderMessageText"
import classNames from "classnames"
import AckMessageText from "./components/AckMessageText"
import ApiError from "../../../components/ApiError"
import LinkButton from "../../../components/LinkButton"
import { tableViews } from "../../../constants"
import SelectInput from "../../../components/SelectInput"
import { useSearchParams } from "react-router-dom"
import { Files, FileText, Info, MagnifyingGlass } from "@phosphor-icons/react"

const OrderTracking = () => {
  const {
    mutate: getOrders,
    isPending,
    status,
    isError,
    error,
  } = useOrdersByAccessionNumber()
  const [searchParams] = useSearchParams()
  const patientIssuer = searchParams.get("patientIssuer") ?? null
  const accessionNumber = searchParams.get("accessionNumber") ?? null
  const [orders, setOrders] = useState([])
  const [results, setResults] = useState([])
  const [reportDetail, setReportDetail] = useState({})
  const [orderDetail, setOrderDetail] = useState({})
  const [ackDetail, setAckDetail] = useState({})

  const [orderTableView, setOrderTableView] = useState(tableViews.BASIC)
  const issuerOptions = [
    "USRS",
    "AKUMIN",
    "BEAR",
    "STAT IMAGING",
    "PROFESSIONAL IMAGING",
    "UPRIGHT MRI",
  ]
  const patientIssuersList = [
    ...(patientIssuer && !issuerOptions.includes(patientIssuer)
      ? [patientIssuer]
      : []),
    ...issuerOptions,
  ].map((value) => ({ label: value, value }))

  const formik = useFormik({
    initialValues: {
      accessionNumber: accessionNumber || "",
      patientIssuer: patientIssuer || "USRS",
    },
    validationSchema: orderTrackingSchema,
    onSubmit: (values) => {
      getOrders(values, {
        onSuccess: (data) => {
          const {
            ordersOnMainMirth,
            ordersOnClientMirth,
            resultsOnMainMirth,
            resultsOnClientMirth,
          } = data
          const groupedOrders = []
          ordersOnClientMirth.forEach((ocm) => {
            if (ocm?.OrderRcvdFromCustomer) {
              // Case 1: Order received from customer
              groupedOrders.push(ocm)
            }
            if (ocm?.OrderSentToMainMirth) {
              // Case 2: Order sent to Main Mirth
              groupedOrders.push(ocm)
              const correspondingOrders = ordersOnMainMirth?.filter(
                (omm) =>
                  ocm.OrderId === omm.FillerOrderId &&
                  ocm.OrderTextId === omm.FillerOrderTextId
              )
              groupedOrders.push(...correspondingOrders)
            }
          })

          const groupedResults = resultsOnMainMirth?.map((rmm) => {
            if (rmm?.ReportType !== "PRELIMINARY") {
              const matchedResult = resultsOnClientMirth?.find(
                (rcm) => rmm?.InboundMessageId === rcm?.FillerResultId
              )
              if (!isEmpty(matchedResult)) {
                return {
                  ...rmm,
                  ...matchedResult,
                }
              }
            }
            return rmm
          })

          const updatedOrders = groupedOrders?.map(toOrder)
          const updatedResults = groupedResults?.map(toResult)

          const toMs = (iso) => {
            const t = iso ? Date.parse(iso) : NaN
            return Number.isNaN(t) ? Infinity : t
          }
          const reportTypeRank = { PRELIMINARY: 0, FINAL: 1, ADDENDUM: 2 }
          const rankOf = (type) =>
            reportTypeRank[String(type).toUpperCase()] ?? 3
          const sortedResults = [...(updatedResults || [])].sort((a, b) => {
            const rankDiff = rankOf(a?.ReportType) - rankOf(b?.ReportType)
            if (rankDiff !== 0) return rankDiff
            return toMs(a?.RcvdFromRSAt) - toMs(b?.RcvdFromRSAt)
          })

          setOrders(updatedOrders)
          setResults(sortedResults)
        },
        onError: console.error,
      })
    },
    validateOnChange: true,
  })

  const { values, setFieldValue, isValid, submitForm } = formik

  useEffect(() => {
    if (values.patientIssuer && values.accessionNumber) {
      submitForm()
    }
  }, [submitForm])

  const order = orders?.[0] ?? {}
  const result = results?.[0] ?? {}

  return (
    <div className="my-8 max-w-9xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-extrabold text-primary tracking-tight">
          Order Tracking
        </h1>
        <p className="mt-3 text-lg text-gray-500 max-w-2xl mx-auto">
          Track and monitor orders through the system&apos;s pipeline
        </p>
      </div>

      <FormikProvider value={formik}>
        <div className="mx-auto">
          <Form className="flex gap-4 mt-6 mb-10 bg-white p-6 rounded-xl shadow-md">
            <SelectInput
              className="max-w-[120px]"
              options={patientIssuersList}
              value={values.patientIssuer}
              onChange={(e) => setFieldValue("patientIssuer", e.target.value)}
            />
            <Field
              className="w-full outline-0 focus:border-secondary focus:ring-2 focus:ring-secondary/20 transition-all border border-slate-300 p-3.5 rounded-lg text-gray-900 ring-gray-300 placeholder:text-gray-400"
              name="accessionNumber"
              placeholder="Enter Accession Number"
              type="text"
              value={values.accessionNumber}
              onChange={(e) => setFieldValue("accessionNumber", e.target.value)}
            />

            <button
              className={cn(
                "flex justify-center items-center gap-4 text-lg bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg text-nowrap transition-all",
                "shadow-md hover:shadow-lg",
                "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-md"
              )}
              disabled={!values.accessionNumber || !isValid || isPending}
              type="submit"
            >
              {isPending && <Loader className="h-5 w-5" />}
              {isPending ? "Tracking" : "Track Order"}
            </button>
          </Form>

          <section>
            {status === "success" ? (
              <div className="flex flex-col border rounded-xl shadow-lg overflow-hidden transition-all duration-300 bg-white">
                <div className="flex flex-wrap md:flex-nowrap gap-6 p-6 bg-gradient-to-r from-primary/5 to-secondary/5">
                  <LabeledText
                    className="flex-1 bg-white p-3 rounded-lg shadow-sm"
                    label="Order ID"
                    value={order?.OrderId ?? "N/A"}
                  />
                  <LabeledText
                    className="flex-1 bg-white p-3 rounded-lg shadow-sm"
                    label="Accession No"
                    value={order?.AccessionNo ?? result?.AccessionNo}
                  />
                  <LabeledText
                    className="flex-1 bg-white p-3 rounded-lg shadow-sm"
                    label="Patient Issuer"
                    value={order?.PatientIssuer ?? result?.PatientIssuer}
                  />
                </div>

                <div className="flex flex-col items-start gap-6 bg-white px-6 py-4">
                  <div className="flex flex-col gap-3 w-full mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <h2 className="text-xl font-bold text-primary flex items-center">
                        <Files className="h-6 w-6 mr-2" weight="bold" />
                        Order Information
                      </h2>

                      <div className="flex items-center gap-2 bg-gray-100 p-2 rounded-lg">
                        <p className="font-medium text-[14px] text-gray-700">
                          Table View:
                        </p>
                        <p
                          className={`text-sm px-2 py-1 rounded ${orderTableView === tableViews.BASIC ? "bg-secondary text-white" : "text-gray-600"}`}
                        >
                          Basic
                        </p>
                        <label
                          className="flex gap-3 items-center cursor-pointer"
                          htmlFor="toggle"
                        >
                          <div className="relative">
                            <input
                              checked={orderTableView === tableViews.EXTENDED}
                              className="sr-only peer"
                              id="toggle"
                              type="checkbox"
                              onChange={(e) =>
                                setOrderTableView(
                                  e.target.checked
                                    ? tableViews.EXTENDED
                                    : tableViews.BASIC
                                )
                              }
                            />

                            <div className="block w-10 h-6 rounded-full bg-gray-300 peer-checked:bg-secondary transition-colors"></div>
                            <div className="dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-full"></div>
                          </div>
                        </label>
                        <p
                          className={`text-sm px-2 py-1 rounded ${orderTableView === tableViews.EXTENDED ? "bg-secondary text-white" : "text-gray-600"}`}
                        >
                          Extended
                        </p>
                      </div>
                    </div>

                    {orders?.length ? (
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="w-full border-collapse min-w-full">
                          <thead>
                            <tr className="bg-primary">
                              <HeaderCell className="py-3 px-4 text-left  text-white">
                                Rcvd From Customer
                              </HeaderCell>
                              <HeaderCell className="py-3 px-4 text-left text-white">
                                Ack Sent To Customer
                              </HeaderCell>
                              {orderTableView === tableViews.EXTENDED && (
                                <HeaderCell className="py-3 px-4 text-left text-white">
                                  Sent To Main Mirth
                                </HeaderCell>
                              )}
                              {orderTableView === tableViews.EXTENDED && (
                                <HeaderCell className="py-3 px-4 text-left text-white">
                                  Ack Rcvd From Main Mirth
                                </HeaderCell>
                              )}
                              <HeaderCell className="py-3 px-4 text-left text-white">
                                Rcvd From Client Mirth
                              </HeaderCell>
                              {orderTableView === tableViews.EXTENDED && (
                                <HeaderCell className="py-3 px-4 text-left text-white">
                                  Ack Sent To Client Mirth
                                </HeaderCell>
                              )}
                              <HeaderCell className="py-3 px-4 text-left text-white">
                                Sent To PACS
                              </HeaderCell>
                              <HeaderCell className="py-3 px-4 text-left text-white">
                                Ack Rcvd From PACS
                              </HeaderCell>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {orders?.map((o, idx) => (
                              <tr
                                key={o?.OrderId + `_${idx}`}
                                className="hover:bg-blue-50 transition-colors duration-150"
                              >
                                <DataCell
                                  className={classNames("py-3 px-4", {
                                    "text-center":
                                      !o?.orderReceivedFromCustomerAt,
                                  })}
                                >
                                  {o?.orderReceivedFromCustomerAt ? (
                                    <LinkButton
                                      className="text-secondary hover:text-secondary_dark font-medium"
                                      onClick={() =>
                                        setOrderDetail({
                                          OrderId: o?.OrderId,
                                          OrderTextId: o?.OrderTextId,
                                          source: "client-mirth",
                                        })
                                      }
                                    >
                                      {o?.orderReceivedFromCustomerAt}
                                    </LinkButton>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </DataCell>

                                <DataCell
                                  className={classNames("py-3 px-4", {
                                    "text-center": !o?.ackSentToCustomerAt,
                                  })}
                                >
                                  {o?.ackSentToCustomerAt ? (
                                    <LinkButton
                                      className="text-secondary hover:text-secondary_dark font-medium"
                                      onClick={() =>
                                        setAckDetail({
                                          OrderId: o?.OrderId,
                                          OrderAckId: o?.OrderAckId,
                                          source: "client-mirth",
                                        })
                                      }
                                    >
                                      {o?.ackSentToCustomerAt}
                                    </LinkButton>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </DataCell>

                                {orderTableView === tableViews.EXTENDED && (
                                  <DataCell
                                    className={classNames("py-3 px-4", {
                                      "text-center": !o?.orderSentToMainMirthAt,
                                    })}
                                  >
                                    {o?.orderSentToMainMirthAt ? (
                                      <LinkButton
                                        className="text-secondary hover:text-secondary_dark font-medium"
                                        onClick={() =>
                                          setOrderDetail({
                                            OrderId: o?.OrderId,
                                            OrderTextId: o?.OrderTextId,
                                            source: "client-mirth",
                                          })
                                        }
                                      >
                                        {o?.orderSentToMainMirthAt}
                                      </LinkButton>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </DataCell>
                                )}

                                {orderTableView === tableViews.EXTENDED && (
                                  <DataCell
                                    className={classNames("py-3 px-4", {
                                      "text-center":
                                        !o?.ackReceivedFromMainMirthAt,
                                    })}
                                  >
                                    {o?.ackReceivedFromMainMirthAt ? (
                                      <LinkButton
                                        className="text-secondary hover:text-secondary_dark font-medium"
                                        onClick={() =>
                                          setAckDetail({
                                            OrderId: o?.OrderId,
                                            OrderAckId: o?.OrderAckId,
                                            source: "client-mirth",
                                          })
                                        }
                                      >
                                        {o?.ackReceivedFromMainMirthAt}
                                      </LinkButton>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </DataCell>
                                )}

                                <DataCell
                                  className={classNames("py-3 px-4", {
                                    "text-center":
                                      !o?.orderReceivedFromClientAt,
                                  })}
                                >
                                  {o?.orderReceivedFromClientAt ? (
                                    <LinkButton
                                      className="text-secondary hover:text-secondary_dark font-medium"
                                      onClick={() =>
                                        setOrderDetail({
                                          OrderId: o?.OrderId,
                                          OrderTextId: o?.OrderTextId,
                                          source: "main-mirth",
                                        })
                                      }
                                    >
                                      {o?.orderReceivedFromClientAt}
                                    </LinkButton>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </DataCell>
                                {orderTableView === tableViews.EXTENDED && (
                                  <DataCell
                                    className={classNames("py-3 px-4", {
                                      "text-center": !o?.ackSentToClientAt,
                                    })}
                                  >
                                    {o?.ackSentToClientAt ? (
                                      <LinkButton
                                        className="text-secondary hover:text-secondary_dark font-medium"
                                        onClick={() =>
                                          setAckDetail({
                                            OrderId: o?.OrderId,
                                            OrderAckId: o?.OrderAckId,
                                            source: "main-mirth",
                                          })
                                        }
                                      >
                                        {o?.ackSentToClientAt}
                                      </LinkButton>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </DataCell>
                                )}
                                <DataCell
                                  className={classNames("py-3 px-4", {
                                    "text-center": !o?.orderSentToRamsoftAt,
                                  })}
                                >
                                  {o?.orderSentToRamsoftAt ? (
                                    <LinkButton
                                      className="text-secondary hover:text-secondary_dark font-medium"
                                      onClick={() =>
                                        setOrderDetail({
                                          OrderId: o?.OrderId,
                                          OrderTextId: o?.OrderTextId,
                                          source: "main-mirth",
                                        })
                                      }
                                    >
                                      {o?.orderSentToRamsoftAt}
                                    </LinkButton>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </DataCell>
                                <DataCell
                                  className={classNames("py-3 px-4", {
                                    "text-center": !o?.ackReceivedFromRamsoftAt,
                                  })}
                                >
                                  {o?.ackReceivedFromRamsoftAt ? (
                                    <LinkButton
                                      className="text-secondary hover:text-secondary_dark font-medium"
                                      onClick={() =>
                                        setAckDetail({
                                          OrderId: o?.OrderId,
                                          OrderAckId: o?.OrderAckId,
                                          source: "main-mirth",
                                        })
                                      }
                                    >
                                      {o?.ackReceivedFromRamsoftAt}
                                    </LinkButton>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </DataCell>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-500 flex items-center">
                          <Info
                            className="h-5 w-5 mr-2 text-gray-400"
                            weight="bold"
                          />
                          No order found
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-3 w-full mt-4">
                    <h2 className="text-xl font-bold text-primary flex items-center">
                      <FileText className="h-6 w-6 mr-2" weight="bold" />
                      Results Information
                    </h2>
                    {results.length ? (
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="w-full border-collapse min-w-full">
                          <thead>
                            <tr className="bg-primary">
                              <HeaderCell className="py-3 px-4 text-left text-white">
                                Report Type
                              </HeaderCell>
                              <HeaderCell className="py-3 px-4 text-left text-white">
                                Received from Ramsoft
                              </HeaderCell>
                              <HeaderCell className="py-3 px-4 text-left text-white">
                                Ack Sent to Ramsoft
                              </HeaderCell>
                              <HeaderCell className="py-3 px-4 text-left text-white">
                                Processed Report Sent to Ramsoft
                              </HeaderCell>
                              <HeaderCell className="py-3 px-4 text-left text-white">
                                Ack Received from Ramsoft
                              </HeaderCell>
                              <HeaderCell className="py-3 px-4 text-left text-white">
                                Received on Client Mirth
                              </HeaderCell>
                              {orderTableView === tableViews.EXTENDED && (
                                <HeaderCell className="py-3 px-4 text-left text-white">
                                  Ack Sent to Main Mirth
                                </HeaderCell>
                              )}
                              <HeaderCell className="py-3 px-4 text-left text-white">
                                Sent to Customer
                              </HeaderCell>
                              <HeaderCell className="py-3 px-4 text-left text-white">
                                Ack Received from Customer
                              </HeaderCell>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {results?.map((r, idx) => (
                              <tr
                                key={r?.InboundMessageId + `_${idx}`}
                                className="hover:bg-blue-50 transition-colors duration-150"
                              >
                                <DataCell className="py-3 px-4">
                                  <button
                                    className="bg-secondary text-white py-1 px-3 rounded-full text-sm font-medium hover:bg-secondary_dark transition-colors"
                                    type="button"
                                    onClick={() => {
                                      setReportDetail({
                                        InboundMessageId: r.InboundMessageId,
                                        InboundMessageTextId:
                                          r.InboundMessageTextId,
                                      })
                                    }}
                                  >
                                    {r?.ReportType}
                                  </button>
                                </DataCell>
                                <DataCell className="py-3 px-4">
                                  {r?.receivedFromRamsoftAt || (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </DataCell>
                                <DataCell className="py-3 px-4">
                                  {r?.ackSentToRamsoftAt || (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </DataCell>
                                <DataCell className="py-3 px-4">
                                  {r?.updatedReportSentToRamsoftAt || (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </DataCell>
                                <DataCell className="py-3 px-4">
                                  {r?.ackReceivedFromRamsoftAt || (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </DataCell>
                                <DataCell className="py-3 px-4">
                                  {r?.msgReceivedOnClientMirth || (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </DataCell>
                                {orderTableView === tableViews.EXTENDED && (
                                  <DataCell className="py-3 px-4">
                                    {r?.ackSentToMainMirth || (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </DataCell>
                                )}
                                <DataCell className="py-3 px-4">
                                  {r?.msgSentToCustomer || (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </DataCell>
                                <DataCell className="py-3 px-4">
                                  {r?.ackReceivedFromCustomer || (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </DataCell>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-500 flex items-center">
                          <Info
                            className="h-5 w-5 mr-2 text-gray-400"
                            weight="bold"
                          />
                          No results found
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : status === "success" && !orders.length ? (
              <div className="flex flex-col items-center justify-center border border-gray-200 rounded-xl p-12 text-center bg-white shadow-md">
                <FileText
                  className="h-16 w-16 text-gray-300 mb-4"
                  weight="thin"
                />
                <p className="text-xl text-gray-500 font-medium">
                  No order found with this accession number
                </p>
                <p className="text-gray-400 mt-2">
                  Please try with a different accession number
                </p>
              </div>
            ) : status === "idle" || !values.accessionNumber ? (
              <div className="flex flex-col items-center justify-center border border-gray-200 rounded-xl p-12 text-center bg-white shadow-md">
                <MagnifyingGlass
                  className="h-16 w-16 text-gray-300 mb-4"
                  weight="thin"
                />
                <p className="text-xl text-gray-500 font-medium">
                  Please enter Accession Number to track an order
                </p>
                <p className="text-gray-400 mt-2">
                  Enter the accession number in the search field above
                </p>
              </div>
            ) : isError ? (
              <div className="py-8 bg-red-50 border border-red-200 rounded-xl shadow-md">
                <ApiError axiosError={error} />
              </div>
            ) : null}
          </section>
        </div>
      </FormikProvider>

      <Modal
        className="max-w-[90%] h-full bg-white"
        isOpen={!isEmpty(reportDetail)}
      >
        <ReportMessageText
          reportDetail={reportDetail}
          onClose={() => setReportDetail({})}
        />
      </Modal>

      <Modal
        className="max-w-[90%] h-full bg-white"
        isOpen={!isEmpty(orderDetail)}
      >
        <OrderMessageText
          orderDetail={orderDetail}
          PatientIssuer={order?.PatientIssuer ?? null}
          onClose={() => setOrderDetail({})}
        />
      </Modal>

      <Modal
        className="max-w-[90%] h-full bg-white"
        isOpen={!isEmpty(ackDetail)}
      >
        <AckMessageText
          ackDetail={ackDetail}
          PatientIssuer={order?.PatientIssuer ?? null}
          onClose={() => setAckDetail({})}
        />
      </Modal>
    </div>
  )
}

export default OrderTracking
